import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "./button";
import "../../styles/components/modal.css";

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  /** 标题下的一句说明（口径/影响范围），建议必填以降低误操作 */
  subtitle?: ReactNode;
  width?: number;
  /** 底部操作区；不传则不渲染 footer（由内容自己排布） */
  footer?: ReactNode;
  /** 点击遮罩是否关闭，默认 false（表单类弹窗防误触丢失输入） */
  closeOnMask?: boolean;
  zIndex?: number;
  onClose: () => void;
  children: ReactNode;
}

/**
 * 弹窗：与旧前端行为对齐（遮罩关闭默认关闭、Esc 关闭、打开时锁滚动）。
 * 约定：破坏性操作不要用普通 Modal 直接提交，改用 <ConfirmDialog/> 或 <Popconfirm/>。
 */
export function Modal({
  open,
  title,
  subtitle,
  width = 560,
  footer,
  closeOnMask = false,
  zIndex,
  onClose,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-mask" style={zIndex ? { zIndex } : undefined} onClick={closeOnMask ? onClose : undefined}>
      <div
        className="modal-box"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div className="modal-head-main">
            <h3 className="modal-title">{title}</h3>
            {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="modal-close" aria-label="关闭" onClick={onClose}>
            <Icon name="close" size={15} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** danger 用于删除/驳回等不可逆操作（确认按钮变红） */
  tone?: "default" | "danger";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** 二次确认弹窗：所有破坏性/不可逆操作的统一入口 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmText = "确认",
  cancelText = "取消",
  tone = "default",
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      title={title}
      width={420}
      onClose={onCancel}
      zIndex={70}
      footer={
        <>
          <Button onClick={onCancel} disabled={loading}>
            {cancelText}
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmText}
          </Button>
        </>
      }
    >
      {description ? <div className="confirm-desc">{description}</div> : null}
    </Modal>
  );
}

export interface PopconfirmProps {
  /** 触发元素 */
  children: ReactNode;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: "default" | "danger";
  loading?: boolean;
  onConfirm: () => void;
}

/** 气泡确认：表格行内危险操作（删除/驳回）用，避免整屏 Modal 打断 */
export function Popconfirm({
  children,
  title,
  description,
  confirmText = "确认",
  cancelText = "取消",
  tone = "danger",
  loading = false,
  onConfirm,
}: PopconfirmProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <span className="popconfirm-wrap" ref={wrapRef}>
      <span className="popconfirm-trigger" onClick={() => setOpen((v) => !v)}>
        {children}
      </span>
      {open ? (
        <div className="popconfirm" role="dialog">
          <div className="popconfirm-title">{title}</div>
          {description ? <div className="popconfirm-desc">{description}</div> : null}
          <div className="popconfirm-actions">
            <Button size="sm" onClick={() => setOpen(false)} disabled={loading}>
              {cancelText}
            </Button>
            <Button
              size="sm"
              variant={tone === "danger" ? "danger" : "primary"}
              loading={loading}
              onClick={() => {
                onConfirm();
                setOpen(false);
              }}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  );
}

export default Modal;
